import { CommonModule, NgForOf } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, inject, Output, signal, ViewChild, WritableSignal } from '@angular/core';
import { getIdToken } from '@angular/fire/auth';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { Employee } from '../../employee/shared/employee.model';
import { BaseComponent } from '../../shared/base.component';
import { DateUtil } from '../../shared/date_util';
import { ViewDronesState } from '../../shared/state-interface';
import { SiteHeaderComponent } from '../../site-header/site-header.component';
import { CustomerNamesComponent } from "../../ui-components/customer-names/customer-names.component";
import { DealerNamesComponent } from '../../ui-components/dealer-names/dealer-names.component';
import { DroneModelsComponent } from "../../ui-components/drone-models/drone-models.component";
import { DroneService } from '../shared/drone.service';
import { RegisteredDrone } from '../shared/registered-drone.model';
import { TransferredDrone } from '../shared/transferred-drone.model';

@Component({
    selector: 'app-view-drones',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatInputModule,
        MatButtonModule,
        MatExpansionModule,
        MatTableModule,
        MatIconModule,
        MatMenuModule,
        MatPaginatorModule,
        NgForOf,
        SiteHeaderComponent,
        DroneModelsComponent,
        DealerNamesComponent,
        CustomerNamesComponent
    ],
    templateUrl: './view-drones.component.html',
    styleUrl: './view-drones.component.css'
})
export class ViewDronesComponent extends BaseComponent implements AfterViewInit {

    @ViewChild(DroneModelsComponent) droneModelsComponent!: DroneModelsComponent;
    @ViewChild(DealerNamesComponent) dealerNamesComponent!: DealerNamesComponent;
    @ViewChild(CustomerNamesComponent) customerNamesComponent!: CustomerNamesComponent;
    @Output() messageEvent = new EventEmitter<string>();

    private droneService: DroneService = inject(DroneService);

    private static _retrievingDrones = "Retrieving Drones";
    private static _retrievingDronesError = `${ViewDronesComponent._retrievingDrones} error:`;
    private static _deletingDrone = "Deleting Drone";
    private static _deletingDroneError = `${ViewDronesComponent._deletingDrone} error:`;

    protected viewDronesForm = new FormGroup({
        serialNumber: new FormControl(""),
        remoteId: new FormControl(""),
    });

    private _drones: WritableSignal<RegisteredDrone[]> = signal([]);
    private _droneCount: WritableSignal<number> = signal(-1);

    public ngAfterViewInit() {
        const state: ViewDronesState | undefined = this.stateManager.viewDronesState;

        if (state != undefined) {
            this.viewDronesForm = state.viewDronesForm;
            this.droneModelsComponent.restoreState(state.droneModelsComponentState);

            if (this.dealerNamesComponent != undefined) {
                this.dealerNamesComponent.restoreState(state.dealerNamesComponentState!);
            }

            this.customerNamesComponent.restoreState(state.customerNamesComponentState);

            if (this.refresh) {
                this.pageIndex = 0;
                this.droneCount = -1;
                this.listDrones(this.overrideSnackBar);
            } else {
                this._drones.set(state.drones);
                this._droneCount.set(state.droneCount);
                super.restoreState(state.baseComponentState);
            }
        } else {
            this.droneModelsComponent.init();

            if (this.dealerNamesComponent != undefined) {
                this.dealerNamesComponent.init();
            }
            
            this.customerNamesComponent.init();
        }
    }

    public ngOnDestroy() {
        const state: ViewDronesState = {} as ViewDronesState;
        state.viewDronesForm = this.viewDronesForm;
        state.drones = this.drones;
        state.droneCount = this.droneCount;
        state.droneModelsComponentState = this.droneModelsComponent.getState();
        state.dealerNamesComponentState = this.dealerNamesComponent != undefined ?
                                            this.dealerNamesComponent.getState() : undefined;
        state.customerNamesComponentState = this.customerNamesComponent.getState();
        state.baseComponentState = super.getState();
        this.stateManager.viewDronesState = state;
    }

    protected get drones(): RegisteredDrone[] {
        return this._drones();
    }

    protected get droneCount(): number {
        return this._droneCount();
    }

    public isUpdatable(drone: RegisteredDrone): boolean {
        return drone.drone_transfers.length === 0;
    }

    protected isTransferable(drone: RegisteredDrone): boolean {
        let isTransferable = true;

        if (drone.drone_transfers.length === 0) {
            // If not yet transferred, then can only be transferred if we're at
            // least 1 day beyond the manufacture date.
            const manufactureDate = DateUtil.getDateOnlyISOString(drone.manufacture_date);
            const currentDate = DateUtil.getDateOnlyISOString(new Date());

            if (currentDate <= manufactureDate) {
                isTransferable = false;
            }

        } else {
            for (const drone_transfer of drone.drone_transfers) {
                if (drone_transfer.dealer !== null) {
                    // Drone previously transferred to a dealer. Checking if user is
                    // eligible to transfer to a Customer.
                    //
                    // If user is an Employee, then the drones coming from the backend
                    // APIs can be drones across all Dealers, thus we need to perform
                    // an additional check to determine if AeroStar Dynamics is the
                    // Dealer to whom the drone was previously transferred.
                    //
                    // If the user is a Dealer or Dealer Rep, then the drones coming
                    // from the backend APIs are limited to those drones that have
                    // previously been transferred to the Dealer, thus no need to perform
                    // additional checks.

                    if (this.userType === "employee") {
                        const employee: Employee = this.userAccount as Employee;
                        if (drone_transfer.dealer.id !== employee.dealer_id) {
                            isTransferable = false;
                            break;
                        }
                    }
                } else if (drone_transfer.customer !== null) {
                    isTransferable = false;
                    break;
                }
            }
        }

        return isTransferable;
    }

    protected getTransferNotation(drone:RegisteredDrone): string {
        let transferNotation = "";

        for (const drone_transfer of drone.drone_transfers) {
            if (this.userType == "employee") {
                if (drone_transfer.transferred_to_type == 2) {
                    transferNotation = this.addNotation(transferNotation, "Dealer");
                } else if (drone_transfer.transferred_to_type == 4) {
                    transferNotation = this.addNotation(transferNotation, "Customer");
                }
            } else if (drone_transfer.transferred_to_type == 4) {
                if (drone_transfer.customer != null) {
                    const businessName = drone_transfer.customer.business_name;
                    if (businessName != null && businessName.length > 0){
                        transferNotation = businessName;
                    } else {
                        transferNotation = `${drone_transfer.customer.first_name} ${drone_transfer.customer.last_name}`;
                    }
                }
            }
        }

        return transferNotation;
    }

    protected getTransferredToName(drone_transfer: TransferredDrone): string {
        let transferredToName = "";
        const transferredToType = drone_transfer.transferred_to_type;

        if (transferredToType == 2) {
            transferredToName = drone_transfer.dealer != null ? drone_transfer.dealer.name : "Unknown";

        } else if (transferredToType == 4) {
            if (drone_transfer.customer != null) {
                if (drone_transfer.customer.business_name != null
                    && drone_transfer.customer.business_name.length > 0) {
                    transferredToName = drone_transfer.customer.business_name;
                } else {
                    transferredToName = `${drone_transfer.customer.first_name} ${drone_transfer.customer.last_name}`;
                }
            } else {
                transferredToName = "Unknown";
            }
        }  else {
            transferredToName = `Invalid Transferred to Type: ${transferredToType}`;
        }

        return transferredToName;
    }

    protected override handlePageEvent(e: PageEvent): void {
        super.handlePageEvent(e);
        this.listDrones();
    }

    protected async listDrones(): Promise<void>;
    protected async listDrones(overrideSnackBar: string | undefined): Promise<void>;
    protected async listDrones(overrideSnackBar?: string): Promise<void> {

        const currentUser = this.auth.currentUser;

        if (currentUser) {
            if (this.searchParamsDirty()) {
                this.pageIndex = 0;
                this._droneCount.set(-1);
                this.markSearchParams(super.pristine);
            }

            const observable = await this.droneService.getDrones(await getIdToken(currentUser),
                this.getSearchParams(), this.pageSize, this.pageIndex);

            if (overrideSnackBar == undefined) {
                super.showSnackBar(`${ViewDronesComponent._retrievingDrones}...`, -1)
            }

            super.message = "";

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["get-drones"] == "success") {
                        // this.drones is a signal. The view will be refreshed
                        // upon update of the signal.
                        this._drones.set(RegisteredDrone.toRegisteredDrones(json["drones"]));
                        this._droneCount.set(json["drone-count"]);
                        this.message = "";
                        overrideSnackBar == undefined ? super.dismissSnackBar() : super.showSnackBar(overrideSnackBar);
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    overrideSnackBar == undefined ? super.dismissSnackBar() : super.showSnackBar(overrideSnackBar, 30);
                    const message = `${ViewDronesComponent._retrievingDronesError} ${(<Error>error).message}`;
                    super.message = message;
                }
            })
        } else {
            const message = `${ViewDronesComponent._retrievingDronesError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    protected async resetSearch() {
        this.serialNumberCtl.setValue("");
        this.droneModelsComponent.droneModelCtl.setValue("");
        this.remoteIdCtl.setValue("");
        
        if (this.dealerNamesComponent != undefined) {
            this.dealerNamesComponent.dealerCtl.setValue("");
        }

        this.customerNamesComponent.customerCtl.setValue("");
        this.markSearchParams(super.dirty);
        this.listDrones();
    }

    protected async addDrone() {
        this.stateManager.addDroneState = undefined;
        this.router.navigate(["/add-drone"]);
    }

    protected async updateDrone(id: number) {
        this.stateManager.updateDroneState = undefined;
        this.router.navigate([`/update-drone/${id}`]);
    }

    protected async transferDrone(id: number) {
        this.stateManager.transferDroneState = undefined;
        this.router.navigate([`/transfer-drone/${id}`]);
    }

    protected async deleteDrone(id: number, serialNumber: string) {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.droneService.deleteDrone(await getIdToken(currentUser), id);
            super.showSnackBar(`${ViewDronesComponent._deletingDrone}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["delete-drone"] == "success") {
                        if (this.drones.length == 1) {
                            // Nothing left on current page, backup
                            // one page.
                            this.pageIndex -= 1;
                        }

                        this.listDrones("Drone deleted.");
                        this.message = "";
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${ViewDronesComponent._deletingDroneError} ${(<Error>error).message}`;
                    console.error(message);
                    super.showSnackBar(message, 30);
                }
            })
        } else {
            const message = `${ViewDronesComponent._deletingDroneError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    protected searchParamsDirty(): boolean {
        return this.serialNumberCtl.dirty
            || this.remoteIdCtl.dirty
            || this.droneModelsComponent.droneModelCtl.dirty
            || (this.dealerNamesComponent != undefined ? this.dealerNamesComponent.dealerCtl.dirty : false)
            || this.customerNamesComponent.customerCtl.dirty
    }

    protected markSearchParams(func: Function) {
        super.markControl(this.serialNumberCtl, func);
        super.markControl(this.remoteIdCtl, func);
        super.markControl(this.droneModelsComponent.droneModelCtl, func);

        if (this.dealerNamesComponent != undefined) {
            super.markControl(this.dealerNamesComponent.dealerCtl, func);
        }

        super.markControl(this.customerNamesComponent.customerCtl, func);
    }

    private getSearchParams(): any {
        return {
            "serialNumber": this.serialNumberCtl.value,
            "model": (this.droneModelsComponent.selectedModel !== "" ?
                            this.droneModelsComponent.selectedModel : undefined),
            "remoteId": this.remoteIdCtl.value,
            "dealerId": (this.dealerNamesComponent != undefined
                        && this.dealerNamesComponent.selectedDealer > 0 ?
                            this.dealerNamesComponent.selectedDealer : undefined),
            "customerId": (this.customerNamesComponent.selectedCustomer > 0 ?
                            this.customerNamesComponent.selectedCustomer : undefined)
        };
    }

    private addNotation(notations: string, notation: string) {
        return (notations.length > 0 ? `${notations} --> ${notation}` : `--> ${notation}`);
    }

    private get serialNumberCtl(): FormControl {
        return this.viewDronesForm.controls.serialNumber;
    }

    private get remoteIdCtl(): FormControl {
        return this.viewDronesForm.controls.remoteId;
    }

    private set droneCount(value: number) {
        this._droneCount.set(value);
    }
}
